import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  RotateCcw,
  Sliders,
} from "lucide-react";

export interface InternalField {
  key: string;
  label: string;
  group: string;
  required?: boolean;
}

type Mapping = Record<string, string | null>;

interface Props {
  jobId: number;
  headers: string[];
  currentMapping: Mapping;
  suggestedMapping: Mapping;
  sampleRows: Record<string, string>[];
  internalFields: InternalField[];
  isConfirmed: boolean;
  // Optional overrides for non-job (batch) usage. Defaults preserve the
  // original census-step behaviour.
  remapUrl?: string; // default: /api/migration/jobs/${jobId}/census/remap
  invalidateQueryKey?: string; // default: /api/migration/jobs/${jobId}
  formatSummary?: (data: any) => string;
  defaultOpen?: boolean; // expand the panel on first render
  // Source headers that we intentionally skip because the value is computed
  // from a later import step (e.g. "Active Employees" / "Total Employees" come
  // from the Phase 2 census). Distinct from "no Administr equivalent" — these
  // aren't missing features, they're deferred computations. Case-insensitive.
  autoCalculatedHeaders?: string[];
}

const SKIP_VALUE = "__skip__";

function MapperSection({
  title,
  count,
  tone,
  description,
  defaultOpen,
  children,
}: {
  title: string;
  count: number;
  tone: "ok" | "warn" | "muted";
  description?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  if (count === 0) return null;
  const toneClass =
    tone === "ok"
      ? "text-green-800 dark:text-green-300"
      : tone === "warn"
        ? "text-amber-800 dark:text-amber-300"
        : "text-muted-foreground";
  return (
    <div className="rounded-md border bg-white dark:bg-background">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/20"
      >
        <span className={`text-sm font-medium ${toneClass}`}>{title}</span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="space-y-2 border-t p-3">
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          <div className="overflow-hidden rounded border">{children}</div>
        </div>
      )}
    </div>
  );
}

function mappingEqual(a: Mapping, b: Mapping): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of Array.from(keys)) {
    if ((a[k] ?? null) !== (b[k] ?? null)) return false;
  }
  return true;
}

function sampleValuesFor(header: string, rows: Record<string, string>[]): string[] {
  const out: string[] = [];
  for (const r of rows) {
    const v = r[header];
    if (v == null) continue;
    const trimmed = String(v).trim();
    if (!trimmed) continue;
    if (!out.includes(trimmed)) out.push(trimmed);
    if (out.length >= 3) break;
  }
  return out;
}

export function ColumnMapper({
  jobId,
  headers,
  currentMapping,
  suggestedMapping,
  sampleRows,
  internalFields,
  isConfirmed,
  remapUrl,
  invalidateQueryKey,
  formatSummary,
  defaultOpen,
  autoCalculatedHeaders,
}: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [draft, setDraft] = useState<Mapping>(currentMapping);
  const [open, setOpen] = useState(Boolean(defaultOpen));

  const dirty = !mappingEqual(draft, currentMapping);

  // Group internal fields for the select dropdown
  const grouped = useMemo(() => {
    const map = new Map<string, InternalField[]>();
    for (const f of internalFields) {
      const list = map.get(f.group);
      if (list) list.push(f);
      else map.set(f.group, [f]);
    }
    return Array.from(map.entries());
  }, [internalFields]);

  // Coverage: which internal fields are mapped from some source column?
  const coverage = useMemo(() => {
    const covered = new Set<string>();
    for (const target of Object.values(draft)) {
      if (target) covered.add(target);
    }
    return covered;
  }, [draft]);

  const missingRequired = internalFields.filter(
    (f) => f.required && !coverage.has(f.key),
  );

  const effectiveRemapUrl =
    remapUrl ?? `/api/migration/jobs/${jobId}/census/remap`;
  const effectiveQueryKey =
    invalidateQueryKey ?? `/api/migration/jobs/${jobId}`;

  const remap = useMutation({
    mutationFn: async (mapping: Mapping) => {
      const res = await apiRequest("PATCH", effectiveRemapUrl, { mapping });
      return (await res.json()) as {
        job?: unknown;
        batch?: unknown;
        summary?: Record<string, number>;
      };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [effectiveQueryKey] });
      const s = data.summary ?? {};
      const description = formatSummary
        ? formatSummary(data)
        : `${s.employees ?? 0} employees, ${s.plans ?? 0} plans, ${s.enrollments ?? 0} enrollments, ${s.warnings ?? 0} warnings.`;
      toast({
        title: "Mapping applied",
        description,
      });
    },
    onError: (err: any) =>
      toast({
        title: "Remap failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  function setMapping(header: string, value: string) {
    setDraft((prev) => ({
      ...prev,
      [header]: value === SKIP_VALUE ? null : value,
    }));
  }

  function revertToSuggested() {
    setDraft({ ...suggestedMapping });
  }

  // Count buckets for the collapsed-header pills (cheap recompute).
  const mappedCount = headers.filter((h) => draft[h] != null).length;
  const unmappedCount = headers.length - mappedCount;

  return (
    <div className="rounded-md border bg-muted/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/30"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Sliders className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Column mapping</span>
          <span className="text-xs text-muted-foreground">
            · {headers.length} source columns
          </span>
          {mappedCount > 0 && (
            <Badge
              variant="outline"
              className="border-emerald-300 bg-emerald-50 text-xs text-emerald-700"
            >
              {mappedCount} mapped
            </Badge>
          )}
          {unmappedCount > 0 && (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              {unmappedCount} unmapped
            </Badge>
          )}
          {missingRequired.length > 0 && (
            <Badge
              variant="outline"
              className="border-amber-300 bg-amber-50 text-xs text-amber-800"
            >
              <AlertTriangle className="mr-1 h-3 w-3" />
              {missingRequired.length} required missing
            </Badge>
          )}
          {dirty && (
            <Badge
              variant="outline"
              className="border-blue-300 bg-blue-50 text-xs text-blue-800"
            >
              unsaved
            </Badge>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="space-y-4 border-t p-4">
          {(() => {
            const fieldByKey = new Map(
              internalFields.map((f) => [f.key, f] as const),
            );

            // Partition headers into 4 buckets
            const autoCalcSet = new Set(
              (autoCalculatedHeaders ?? []).map((s) => s.trim().toLowerCase()),
            );
            const mappedHeaders: string[] = [];
            const autoCalculatedBucket: string[] = [];
            const autoUnmappedHeaders: string[] = [];
            const userSkippedHeaders: string[] = [];
            for (const h of headers) {
              const target = draft[h] ?? null;
              if (target) {
                mappedHeaders.push(h);
                continue;
              }
              // Unmapped — does it belong in the "auto-calculated" bucket?
              if (autoCalcSet.has(h.trim().toLowerCase())) {
                autoCalculatedBucket.push(h);
                continue;
              }
              const suggested = suggestedMapping[h] ?? null;
              if (suggested === null) autoUnmappedHeaders.push(h);
              else userSkippedHeaders.push(h);
            }

            function renderRows(list: string[]) {
              return list.map((h) => {
                const samples = sampleValuesFor(h, sampleRows);
                const target = draft[h] ?? null;
                const targetField = target ? fieldByKey.get(target) : null;
                const suggested = suggestedMapping[h] ?? null;
                const differs = target !== suggested;
                return (
                  <TableRow key={h}>
                    <TableCell className="font-mono text-xs align-top">
                      <div className="flex items-center gap-2">
                        <span className="break-all">{h}</span>
                        {differs && (
                          <Badge
                            variant="outline"
                            className="shrink-0 text-[10px]"
                          >
                            edited
                          </Badge>
                        )}
                      </div>
                      {targetField && (
                        <div className="mt-1 text-[10px] font-sans font-normal text-muted-foreground">
                          → {targetField.label}
                          {targetField.required && (
                            <span className="text-red-500"> *</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Select
                        value={target ?? SKIP_VALUE}
                        onValueChange={(v) => setMapping(h, v)}
                        disabled={isConfirmed || remap.isPending}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          <SelectItem
                            value={SKIP_VALUE}
                            className="text-xs text-muted-foreground"
                          >
                            — Skip this column —
                          </SelectItem>
                          {grouped.map(([group, fields]) => (
                            <SelectGroup key={group}>
                              <SelectLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                {group}
                              </SelectLabel>
                              {fields.map((f) => (
                                <SelectItem
                                  key={f.key}
                                  value={f.key}
                                  className="text-xs pl-4"
                                >
                                  {f.label}
                                  {f.required ? " *" : ""}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground align-top">
                      {samples.length > 0 ? (
                        samples.map((s, i) => (
                          <span
                            key={i}
                            className="mr-1 inline-block max-w-[160px] truncate align-bottom"
                            title={s}
                          >
                            {s}
                            {i < samples.length - 1 ? "," : ""}
                          </span>
                        ))
                      ) : (
                        <span className="italic">empty</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              });
            }

            return (
              <>
                {/* Required-field warning — not duplicated by the sub-tabs.
                    Per-bucket counts live on the TabsTrigger pills below. */}
                {missingRequired.length > 0 && (
                  <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs font-medium text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {missingRequired.length} required field
                    {missingRequired.length === 1 ? "" : "s"} missing:{" "}
                    <span>
                      {missingRequired.map((f) => f.label).join(", ")}
                    </span>
                  </div>
                )}

                {/* Sub-tabs: switch between mapped / skipped / no-equivalent */}
                <Tabs defaultValue="mapped">
                  <TabsList className="w-full justify-start">
                    <TabsTrigger value="mapped" className="gap-2">
                      Mapped
                      <Badge
                        variant="outline"
                        className="border-emerald-300 bg-emerald-50 text-[10px] text-emerald-700"
                      >
                        {mappedHeaders.length}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="skipped" className="gap-2">
                      Skipped by you
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          userSkippedHeaders.length > 0
                            ? "border-amber-300 bg-amber-50 text-amber-800"
                            : "text-muted-foreground"
                        }`}
                      >
                        {userSkippedHeaders.length}
                      </Badge>
                    </TabsTrigger>
                    {autoCalculatedBucket.length > 0 && (
                      <TabsTrigger value="auto-calc" className="gap-2">
                        Auto-calculated
                        <Badge
                          variant="outline"
                          className="border-blue-300 bg-blue-50 text-[10px] text-blue-700"
                        >
                          {autoCalculatedBucket.length}
                        </Badge>
                      </TabsTrigger>
                    )}
                    <TabsTrigger value="no-equiv" className="gap-2">
                      No Administr equivalent
                      <Badge
                        variant="outline"
                        className="text-[10px] text-muted-foreground"
                      >
                        {autoUnmappedHeaders.length}
                      </Badge>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="mapped" className="mt-3">
                    {mappedHeaders.length === 0 ? (
                      <p className="rounded border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
                        No mapped columns yet.
                      </p>
                    ) : (
                      <div className="overflow-hidden rounded border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-60">Source column</TableHead>
                              <TableHead className="w-72">Administr field</TableHead>
                              <TableHead>Sample values</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>{renderRows(mappedHeaders)}</TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="skipped" className="mt-3">
                    {userSkippedHeaders.length === 0 ? (
                      <p className="rounded border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
                        Nothing skipped — every source column either maps to a
                        field or has no Administr equivalent.
                      </p>
                    ) : (
                      <div className="overflow-hidden rounded border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-60">Source column</TableHead>
                              <TableHead className="w-72">Map to…</TableHead>
                              <TableHead>Sample values</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>{renderRows(userSkippedHeaders)}</TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>

                  {autoCalculatedBucket.length > 0 && (
                    <TabsContent value="auto-calc" className="mt-3 space-y-2">
                      <p className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-200">
                        These columns are calculated automatically from census
                        data after you import employees. No mapping needed — but
                        you can override any of them below if you want to map to
                        a different field.
                      </p>
                      <div className="overflow-hidden rounded border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-60">Source column</TableHead>
                              <TableHead className="w-72">Map to… (optional)</TableHead>
                              <TableHead>Sample values</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>{renderRows(autoCalculatedBucket)}</TableBody>
                        </Table>
                      </div>
                    </TabsContent>
                  )}

                  <TabsContent value="no-equiv" className="mt-3 space-y-2">
                    {autoUnmappedHeaders.length === 0 ? (
                      <p className="rounded border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
                        Every source column has a matching Administr field.
                      </p>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground">
                          These source columns carry data we don't currently
                          store. You can map them to any internal field below;
                          otherwise they'll be ignored on confirm.
                        </p>
                        <div className="overflow-hidden rounded border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-60">Source column</TableHead>
                                <TableHead className="w-72">Map to… (optional)</TableHead>
                                <TableHead>Sample values</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>{renderRows(autoUnmappedHeaders)}</TableBody>
                          </Table>
                        </div>
                      </>
                    )}
                  </TabsContent>
                </Tabs>

                {/* Action row */}
                <div className="flex items-center justify-end gap-2">
                  {!isConfirmed && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={revertToSuggested}
                        disabled={
                          remap.isPending ||
                          mappingEqual(draft, suggestedMapping)
                        }
                      >
                        <RotateCcw className="mr-1 h-3 w-3" /> Revert to suggested
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => remap.mutate(draft)}
                        disabled={!dirty || remap.isPending}
                      >
                        {remap.isPending && (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        )}
                        Apply mapping
                      </Button>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
